<template>
  <div class="w-full flex flex-col p-1 gap-5">
    <div class="flex items-center gap-2 py-1">
      <InfoResponseIcon class="text-icon-normal" />
      <BaseText type="body" size="sm" class="text-text-muted">
        Only brands whose ads you have saved will be displayed here. Explore all brands in Spyder.
      </BaseText>
    </div>
    <!-- While searching, an empty favorites list means no matches rather than no favorites -->
    <BrandsList
      :brands="brands"
      :brand-total="brandTotal"
      :loading-brands="loadingBrands"
      :favorite-filter="favoriteFilter && !searchQuery"
      :infinite-id="infiniteId"
      @favorite="favoriteBrand($event)"
      @get-more-brands="getMoreBrands($event)"
    />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { getBrandPrimaryMetaPageId } from '@/utils/spyder/brandUtils'
import { getUniqueSocialNetworks } from '@/utils/spyder/brandSocialsUtils'
import BrandsList from '../brands/BrandsList.vue'

import InfoResponseIcon from '../globals/Icons/ResponseAlertIcons/InfoResponseIcon.vue'

const PAGE_SIZE = 20

// Adds what the brands table renders beyond the Spyder brand
const toBrandRow = (brand) => ({
  ...brand,
  avatar: brand.avatar_url,
  socialNetworks: getUniqueSocialNetworks(brand.socials || []),
  industry: brand.enrichment?.industry || null,
  businessModel: brand.enrichment?.business_model || null,
  adLibraryId: getBrandPrimaryMetaPageId(brand),
  isFavorited: brand.is_favorited === true
})

export default {
  name: 'LibraryBrandView',
  components: {
    BrandsList,
    InfoResponseIcon,
  },
  props: {
    // Advertisements
    favoriteFilter: {
      type: Boolean,
      default: () => false
    },
    textSearch: {
      type: String,
      default: () => ''
    },
    updateQuery: {
      type: Number,
      default: () => null
    }
  },
  data () {
    return {
      brands: [],
      brandTotal: 0,
      loadingBrands: false,
      nextPage: null,
      latestRequestId: 0,
      infiniteId: +new Date()
    }
  },
  computed: {
    searchQuery () {
      return this.textSearch.trim()
    }
  },
  watch: {
    updateQuery (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchBrands()
      }
    },
    favoriteFilter (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchBrands()
      }
    }
  },
  methods: {
    async favoriteBrand (brandId) {
      const brand = this.brands.find(brand => brand.id === brandId)
      if (!brand) return

      // Toggled straight away, and reverted if the request fails
      const isFavorited = !brand.isFavorited
      brand.isFavorited = isFavorited

      try {
        if (isFavorited) {
          await ForeplayAPI.Brands.favoriteLibraryBrand(brandId)
        } else {
          await ForeplayAPI.Brands.unfavoriteLibraryBrand(brandId)
        }
      } catch (error) {
        brand.isFavorited = !isFavorited
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: `Error ${isFavorited ? 'favoriting' : 'unfavoriting'} brand. See console for details`
        })
      }
    },
    async fetchBrands (load = true) {
      // Drop pages still loading for the previous list
      this.latestRequestId++
      this.brands = []
      this.loadingBrands = load

      try {
        const { data, total, pages } = await ForeplayAPI.Brands.getLibraryBrands({
          limit: PAGE_SIZE,
          page: 1,
          favorites: this.favoriteFilter,
          search: this.searchQuery
        })

        this.brands = (data || []).map(toBrandRow)
        this.brandTotal = total || 0
        this.nextPage = pages > 1 ? 2 : null
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error fetching brands. See console for details'
        })
      } finally {
        this.loadingBrands = false
        this.infiniteId += 1
      }

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 600)
    },
    async getMoreBrands ($state) {
      if (!this.nextPage) {
        $state.complete()
        return
      }

      const requestId = this.latestRequestId
      const page = this.nextPage

      try {
        const { data, pages } = await ForeplayAPI.Brands.getLibraryBrands({
          limit: PAGE_SIZE,
          page,
          favorites: this.favoriteFilter,
          search: this.searchQuery
        })
        if (requestId !== this.latestRequestId) return

        this.brands = this.brands.concat((data || []).map(toBrandRow))
        this.nextPage = page < pages ? page + 1 : null
        $state.loaded()
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error fetching more brands. See console for details'
        })
        $state.complete()
      }
    }
  }
}
</script>

<style scoped lang="sass">
</style>
